import React, { useEffect, useRef, useState } from "react";
import { Image, Platform, SectionList, StyleSheet, Text, TextInput, TouchableOpacity, View } from "react-native";
import * as ImSdk from "../../../imsdk/api";
import { GroupSummary } from "../../../imsdk/db/t_group_summary";
import { useIsFocused } from "@react-navigation/native";
import { orderListByLetter } from "../../utils/utils";
import { LeftView } from "../Chat/CListItem";
import { color_BEBEBE, color_F1F1F1, color_main, primary50 } from "../../assets/style/colors";
import { ListEmpty } from "../../components/ListEmpty";
import { MainNavProps } from "../navigation/main/MainParamList";
import BaseScreen from "../BaseScreen";
import NavBar from "../../components/NavBar";
import { useNavigationToChatView2 } from "../Chat/chat-helper";
import { Str_GroupChat, Str_Search } from "../../language/string_res";
import { hideLoading, showLoading } from "../../components/Loading";
import { TopicWithSummary } from "../../../imsdk/api/topic";
import { useStore } from "../../store/UserStore";
import { MQEventCallBack } from "../../../imsdk/api/mq";
import time from "../../../imsdk/time";
import { defaultTopic, Topic } from "../../../imsdk/db/t_topic";

/** 通讯录群列表可能仅有 group_summary、尚无会话列表里的 TopicWithSummary：构造可进聊天的最小会话 */
async function buildTopicStubFromSummary(
    item: GroupSummary,
    im: ReturnType<typeof ImSdk.getInstance>,
): Promise<TopicWithSummary> {
    const gl = await im.GetGroupLocal(item.tid);
    const now = time.toUnixMillSecond(time.now());
    const topic: Topic = {
        ...defaultTopic,
        tid: item.tid,
        uid: item.uid ?? gl.group?.uid ?? 0,
        name: gl.group?.name || item.name || "",
        cid: "",
        ctime: item.ctime ?? now,
        type: "group",
        sid: 0,
        hidemid: "",
        order: 0,
        lvTime: 0,
        lsMid: "",
        remark: (gl.group?.remark ?? item.remark) || "",
        mute: false,
        draft: "",
        unread: 0,
        msg_synced: 0,
        bridgeState: 0,
        bridgeCursor: "",
        bridgePages: 0,
        bridgeEpoch: 0,
        bridgeUpdatedAt: 0,
        lutime: now,
        lctime: now,
    };
    return {
        topic,
        group: gl.group,
        users: gl.members ?? [],
    };
}

type SectionProps = {
    title: string;
    data: GroupSummary[];
};

export const GroupList = ({ }: MainNavProps<"GroupList">) => {
    const sectionRef = useRef<SectionList<GroupSummary, SectionProps>>(null);
    const [section, setSection] = useState<SectionProps[]>([]);
    const [refresh, setRefresh] = useState(false);
    const { state } = useStore()
    const toChatView = useNavigationToChatView2();
    const im = ImSdk.getInstance();
    const [searchVal, setSearchVal] = useState<string>("");
    const groupSummaryRef = useRef<GroupSummary[]>([]);
    const isLoaded = useRef<boolean>(false); // 是否加载过数据
    const isFocused = useIsFocused();
    useEffect((() => {
        showLoading()
        im.GetGroupSummaryList(true)
            .then((res) => {
                if (res.error) {
                    return;
                }
                if (res.groupSummarys) {
                    groupSummaryRef.current = res.groupSummarys;
                    const result = orderListByLetter(res.groupSummarys);
                    setSection(result);
                    setRefresh(true)
                }
            })
            .finally(() => {
                hideLoading()
                isLoaded.current = true;
            });
    }), []);
    const imcallback: MQEventCallBack = async (res) => {
        let tid
        if (res.type === "group_update") {
            tid = res.data.group_update?.tid || "";
        } else if (res.type === "topic_remark") {
            tid = res.data.topic_remark?.tid || "";
        } else if (res.type === 'topic_update') {
            tid = res.data.topic_update?.tid || "";
        }
        if (!tid) return;

        const updateRes = await im.GetGroupSummary(tid);
        const updated = updateRes.groupSummary;
        if (!updated) return;

        groupSummaryRef.current = groupSummaryRef.current.map((item) =>
            item.tid === updated.tid ? updated : item
        );

        setSection((prev) => {
            const newSections = prev.map((section) => {
                const newData = section.data.map((item) =>
                    item.tid === updated.tid ? updated : item
                );
                return { ...section, data: newData };
            });

            return newSections;
        });
    };

    useEffect(() => {
        im.on("group_update", imcallback);
        im.on("topic_update", imcallback);
        im.on("topic_remark", imcallback);
        return () => {
            im.off("*", imcallback);
        };
    }, []);

    useEffect((() => {
        if (isFocused && isLoaded.current) {
            const all = groupSummaryRef.current || [];
            const filterData =
                searchVal && searchVal.length
                    ? all.filter((v) => (v.name ?? "").indexOf(searchVal) !== -1)
                    : all;
            const result = orderListByLetter(filterData);
            setSection(result);
        }
    }), [isFocused, searchVal, refresh]);




    const onClick = async (item: GroupSummary) => {
        const tid = item.tid;
        const topic = findTopic(tid);
        if (topic) {
            await toChatView(topic);
            return;
        }
        const local = await im.GetTopicDetail(tid, false);
        if (local.topic) {
            await toChatView(local.topic);
            void im.GetTopicDetail(tid, true);
            return;
        }
        const stub = await buildTopicStubFromSummary(item, im);
        await toChatView(stub);
        void im.GetTopicDetail(tid, true);
    };

    const findTopic = (tid: string) => {
        let topic: TopicWithSummary | undefined;
        if (state?.topicSummaryList.top) {
            const topics = state.topicSummaryList.top.filter((v) => v.topic.tid === tid);
            if (topics.length > 0) {
                topic = topics[0];
            }
        }
        if (!topic && state?.topicSummaryList.normal) {
            const topics = state.topicSummaryList.normal.filter((v) => v.topic.tid === tid);
            if (topics.length > 0) {
                topic = topics[0];
            }
        }
        return topic;
    };
    const renderItem = ({ item }: { item: GroupSummary }) => {
        return (<TouchableOpacity
            onPress={() => onClick(item)}
            activeOpacity={1} style={styles.item}>
            <LeftView
                left={{ name: '', avatar: item.avatar, uid: 0 }}
                size={50}
                isRounded={true}
                isGroup={true}
            />
            <Text numberOfLines={1} ellipsizeMode={"tail"} style={styles.itemText}>{item.remark || item.name || Str_GroupChat}</Text>
            <View style={styles.itemLine} />
        </TouchableOpacity>
        );
    };

    const SectionHeader = ({ section: { title } }: { section: SectionProps }) => (
        <View style={{ marginTop: 18 }}>
            <Text style={styles.header}>{title}</Text>
        </View>
    );

    return (
        <BaseScreen styles={{ backgroundColor: "white" }}>
            <NavBar
                title={Str_GroupChat}
                showBack={Platform.OS !== 'web'}
                style={{ backgroundColor: "white" }}
            />
            <View style={styles.search}>
                <Image
                    source={require("../../assets/image/icon_search.png")}
                    style={{ width: 20, height: 20, objectFit: "contain" }} />
                <TextInput
                    editable={true}
                    focusable={true}
                    selectionColor={color_main}
                    placeholderTextColor={color_BEBEBE}
                    value={searchVal}
                    onChangeText={text => {
                        setSearchVal(text);
                    }}
                    placeholder={Str_Search}
                    style={styles.searchText}
                />
            </View>
            <SectionList
                sections={section}
                ref={sectionRef}
                keyExtractor={(item) => item.tid.toString()}
                renderItem={renderItem}
                renderSectionHeader={SectionHeader}
                ListEmptyComponent={<ListEmpty />} />
        </BaseScreen>
    );
};
const styles = StyleSheet.create({
    search: {
        borderRadius: 5,
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 10,
        height: 45,
        marginHorizontal: 14,
        backgroundColor: primary50,
        marginTop: 12
    },
    searchText: {
        fontSize: 15,
        color: "#7E7E7E",
        marginStart: 4,
        flex: 1,
        textAlignVertical: 'center',
    },

    item: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 16,
        paddingVertical: 10
    },
    header: {
        fontSize: 13,
        marginStart: 16,
        color: "#737373"
    },
    itemText: {
        fontSize: 17,
        flex: 1,
        color: '#000000',
        fontWeight: 'bold',
        fontFamily: '',
    },
    itemLine: {
        position: "absolute",
        bottom: 0,
        start: 70,
        height: 1,
        end: 0,
        backgroundColor: color_F1F1F1
    }
});

